'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function FinancialCalculator() {
  const t = useTranslations("tools.financialCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState('loan');

  // Loan Calculator State
  const [loanInputs, setLoanInputs] = useState({
    amount: 10000,
    rate: 5,
    term: 5
  });
  const [loanResults, setLoanResults] = useState<any>(null);

  // Compound Interest State
  const [compoundInputs, setCompoundInputs] = useState({
    principal: 5000,
    rate: 7,
    years: 10,
    frequency: 12
  });
  const [compoundResults, setCompoundResults] = useState<any>(null);

  // Mortgage State
  const [mortgageInputs, setMortgageInputs] = useState({
    price: 300000,
    down: 60000,
    rate: 6.5,
    term: 30
  });
  const [mortgageResults, setMortgageResults] = useState<any>(null);

  // Savings State
  const [savingsInputs, setSavingsInputs] = useState({
    goal: 50000,
    initial: 5000,
    monthly: 500,
    rate: 5
  });
  const [savingsResults, setSavingsResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatCurrency = (amount: number) => {
    return '$' + amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  };

  const handleLoanCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = loanInputs.amount;
    const rate = loanInputs.rate / 100 / 12;
    const term = loanInputs.term * 12;

    const monthlyPayment = amount * (rate * Math.pow(1 + rate, term)) / (Math.pow(1 + rate, term) - 1);
    const totalPaid = monthlyPayment * term;
    const totalInterest = totalPaid - amount;

    setLoanResults({
      monthly: monthlyPayment,
      interest: totalInterest,
      total: totalPaid
    });
  };

  const handleCompoundCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const principal = compoundInputs.principal;
    const rate = compoundInputs.rate / 100;
    const years = compoundInputs.years;
    const frequency = compoundInputs.frequency;

    const futureValue = principal * Math.pow(1 + rate / frequency, frequency * years);
    const interestEarned = futureValue - principal;

    setCompoundResults({
      initial: principal,
      earned: interestEarned,
      future: futureValue
    });
  };

  const handleMortgageCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const price = mortgageInputs.price;
    const down = mortgageInputs.down;
    const rate = mortgageInputs.rate / 100 / 12;
    const term = mortgageInputs.term * 12;

    const loanAmount = price - down;
    const monthlyPayment = loanAmount * (rate * Math.pow(1 + rate, term)) / (Math.pow(1 + rate, term) - 1);
    const totalPaid = monthlyPayment * term;
    const totalInterest = totalPaid - loanAmount;
    const totalCost = totalPaid + down;

    setMortgageResults({
      loan: loanAmount,
      monthly: monthlyPayment,
      interest: totalInterest,
      total: totalCost
    });
  };

  const handleSavingsCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const goal = savingsInputs.goal;
    const initial = savingsInputs.initial;
    const monthly = savingsInputs.monthly;
    const rate = savingsInputs.rate / 100 / 12;

    let balance = initial;
    let months = 0;
    let totalContributions = initial;

    while (balance < goal && months < 1200) {
      balance = balance * (1 + rate) + monthly;
      totalContributions += monthly;
      months++;
    }

    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    const timeString = years > 0
      ? `${years} ${t('year')}${years > 1 ? 's' : ''} ${remainingMonths} ${t('month')}${remainingMonths !== 1 ? 's' : ''}`
      : `${remainingMonths} ${t('month')}${remainingMonths !== 1 ? 's' : ''}`;
    const interestEarned = balance - totalContributions;

    setSavingsResults({
      time: timeString,
      contributions: totalContributions,
      interest: interestEarned,
      final: balance
    });
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const subtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '2rem 1rem' },
    { padding: '2.5rem 1.5rem' },
    { padding: '3rem 1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    {}
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const categoryGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1.5rem',
      marginBottom: '3rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const categoryCardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '1rem',
    padding: '1.5rem',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    cursor: 'pointer'
  };

  const categoryHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: '2px solid #e2e8f0'
  };

  const categoryIconStyle = getResponsiveStyle(
    { marginRight: '0.75rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '1.75rem' }
  );

  const categoryTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const calcLinkStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    textDecoration: 'none',
    color: '#475569',
    transition: 'all 0.2s ease',
    fontSize: '0.95rem',
    marginBottom: '0.5rem'
  };

  const quickCalcSectionStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '1rem',
    padding: '2rem',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
    marginTop: '2rem'
  };

  const tabsContainerStyle = getResponsiveStyle(
    {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '2rem',
      justifyContent: 'center' as const
    },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const, flexWrap: 'wrap' as const },
    { flexDirection: 'row' as const, flexWrap: 'wrap' as const }
  );

  const tabBtnStyle = (isActive: boolean) => getResponsiveStyle(
    {
      padding: '0.75rem 1.5rem',
      border: isActive ? 'none' : '2px solid #e2e8f0',
      background: isActive ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontWeight: 500,
      color: isActive ? 'white' : '#64748b',
      transition: 'all 0.3s ease',
      fontSize: '0.95rem'
    },
    { width: '100%' },
    { width: 'auto' },
    { width: 'auto' }
  );

  const formGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1.5rem',
      marginBottom: '1.5rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const formGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column'
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: 500,
    color: '#334155',
    marginBottom: '0.5rem',
    fontSize: '0.95rem'
  };

  const inputStyle: React.CSSProperties = {
    padding: '0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'border-color 0.3s ease'
  };

  const calculateBtnStyle: React.CSSProperties = {
    width: '100%',
    padding: '1rem',
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '0.75rem',
    fontSize: '1.1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
  };

  const resultsStyle: React.CSSProperties = {
    marginTop: '2rem',
    padding: '1.5rem',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    borderRadius: '0.75rem',
    borderLeft: '4px solid #2563eb'
  };

  const resultItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem',
    background: 'white',
    borderRadius: '0.5rem',
    marginBottom: '1rem'
  };

  const resultLabelStyle: React.CSSProperties = {
    color: '#64748b',
    fontWeight: 500
  };

  const resultValueStyle: React.CSSProperties = {
    color: '#2563eb',
    fontWeight: 700,
    fontSize: '1.1rem'
  };

  const resultHighlightStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle: React.CSSProperties = {
    marginLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle: React.CSSProperties = {
    marginBottom: '1.5rem'
  };

  const referenceStyle: React.CSSProperties = {
    fontSize: '0.9rem',
    color: colors.textSecondary,
    marginBottom: '0.5rem'
  };

  const linkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-all'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={subtitleStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={h2Style}>{t("toolTitle")}</h2>
          <p style={subtitleStyle}>{t("toolSubtitle")}</p>
        </div>

        <div style={categoryGridStyle}>
          <div style={categoryCardStyle}>
            <div style={categoryHeaderStyle}>
              <span style={categoryIconStyle}>🏠</span>
              <h3 style={categoryTitleStyle}>{t("mortgageCategory")}</h3>
            </div>
            <div>
              <div style={calcLinkStyle}>{t("mortgageCalculator")}</div>
              <div style={calcLinkStyle}>{t("amortizationCalculator")}</div>
              <div style={calcLinkStyle}>{t("payoffCalculator")}</div>
              <div style={calcLinkStyle}>{t("affordabilityCalculator")}</div>
              <div style={calcLinkStyle}>{t("rentCalculator")}</div>
              <div style={calcLinkStyle}>{t("refinanceCalculator")}</div>
            </div>
          </div>

          <div style={categoryCardStyle}>
            <div style={categoryHeaderStyle}>
              <span style={categoryIconStyle}>📈</span>
              <h3 style={categoryTitleStyle}>{t("investmentCategory")}</h3>
            </div>
            <div>
              <div style={calcLinkStyle}>{t("investmentCalculator")}</div>
              <div style={calcLinkStyle}>{t("compoundInterestCalculator")}</div>
              <div style={calcLinkStyle}>{t("savingsCalculator")}</div>
              <div style={calcLinkStyle}>{t("roiCalculator")}</div>
              <div style={calcLinkStyle}>{t("presentValueCalculator")}</div>
              <div style={calcLinkStyle}>{t("futureValueCalculator")}</div>
            </div>
          </div>

          <div style={categoryCardStyle}>
            <div style={categoryHeaderStyle}>
              <span style={categoryIconStyle}>🏖️</span>
              <h3 style={categoryTitleStyle}>{t("retirementCategory")}</h3>
            </div>
            <div>
              <div style={calcLinkStyle}>{t("retirementCalculator")}</div>
              <div style={calcLinkStyle}>{t("401kCalculator")}</div>
              <div style={calcLinkStyle}>{t("pensionCalculator")}</div>
              <div style={calcLinkStyle}>{t("socialSecurityCalculator")}</div>
              <div style={calcLinkStyle}>{t("rothIraCalculator")}</div>
              <div style={calcLinkStyle}>{t("annuityCalculator")}</div>
            </div>
          </div>

          <div style={categoryCardStyle}>
            <div style={categoryHeaderStyle}>
              <span style={categoryIconStyle}>🚗</span>
              <h3 style={categoryTitleStyle}>{t("autoCategory")}</h3>
            </div>
            <div>
              <div style={calcLinkStyle}>{t("autoLoanCalculator")}</div>
              <div style={calcLinkStyle}>{t("loanCalculator")}</div>
              <div style={calcLinkStyle}>{t("personalLoanCalculator")}</div>
              <div style={calcLinkStyle}>{t("studentLoanCalculator")}</div>
              <div style={calcLinkStyle}>{t("businessLoanCalculator")}</div>
              <div style={calcLinkStyle}>{t("leaseCalculator")}</div>
            </div>
          </div>

          <div style={categoryCardStyle}>
            <div style={categoryHeaderStyle}>
              <span style={categoryIconStyle}>💳</span>
              <h3 style={categoryTitleStyle}>{t("debtCategory")}</h3>
            </div>
            <div>
              <div style={calcLinkStyle}>{t("creditCardCalculator")}</div>
              <div style={calcLinkStyle}>{t("debtPayoffCalculator")}</div>
              <div style={calcLinkStyle}>{t("debtConsolidationCalculator")}</div>
              <div style={calcLinkStyle}>{t("debtRatioCalculator")}</div>
            </div>
          </div>

          <div style={categoryCardStyle}>
            <div style={categoryHeaderStyle}>
              <span style={categoryIconStyle}>💼</span>
              <h3 style={categoryTitleStyle}>{t("taxCategory")}</h3>
            </div>
            <div>
              <div style={calcLinkStyle}>{t("incomeTaxCalculator")}</div>
              <div style={calcLinkStyle}>{t("salaryCalculator")}</div>
              <div style={calcLinkStyle}>{t("takeHomePayCalculator")}</div>
              <div style={calcLinkStyle}>{t("salesTaxCalculator")}</div>
              <div style={calcLinkStyle}>{t("vatCalculator")}</div>
            </div>
          </div>
        </div>

        <div style={quickCalcSectionStyle}>
          <div style={toolHeaderStyle}>
            <h3 style={h2Style}>{t("quickCalculators")}</h3>
            <p style={subtitleStyle}>{t("quickCalculatorsSubtitle")}</p>
          </div>

          <div style={tabsContainerStyle}>
            <button
              style={tabBtnStyle(activeTab === 'loan')}
              onClick={() => setActiveTab('loan')}
            >
              {t("loanPaymentTab")}
            </button>
            <button
              style={tabBtnStyle(activeTab === 'compound')}
              onClick={() => setActiveTab('compound')}
            >
              {t("compoundInterestTab")}
            </button>
            <button
              style={tabBtnStyle(activeTab === 'mortgage')}
              onClick={() => setActiveTab('mortgage')}
            >
              {t("mortgageTab")}
            </button>
            <button
              style={tabBtnStyle(activeTab === 'savings')}
              onClick={() => setActiveTab('savings')}
            >
              {t("savingsGoalTab")}
            </button>
          </div>

          {activeTab === 'loan' && (
            <div>
              <form onSubmit={handleLoanCalculate}>
                <div style={formGridStyle}>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("loanAmount")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={loanInputs.amount}
                      onChange={(e) => setLoanInputs({...loanInputs, amount: parseFloat(e.target.value)})}
                      min="0"
                      step="100"
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("annualInterestRate")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={loanInputs.rate}
                      onChange={(e) => setLoanInputs({...loanInputs, rate: parseFloat(e.target.value)})}
                      min="0"
                      max="100"
                      step="0.1"
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("loanTermYears")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={loanInputs.term}
                      onChange={(e) => setLoanInputs({...loanInputs, term: parseFloat(e.target.value)})}
                      min="1"
                      max="50"
                      required
                    />
                  </div>
                </div>
                <button type="submit" style={calculateBtnStyle}>{t("calculateLoanPayment")}</button>
              </form>
              {loanResults && (
                <div style={resultsStyle}>
                  <h4 style={h3Style}>{t("loanPaymentResults")}</h4>
                  <div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("monthlyPayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(loanResults.monthly)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("totalInterest")}</span>
                      <span style={resultValueStyle}>{formatCurrency(loanResults.interest)}</span>
                    </div>
                    <div style={resultHighlightStyle}>
                      <span style={{...resultLabelStyle, color: 'rgba(255, 255, 255, 0.9)'}}>{t("totalAmountPaid")}</span>
                      <span style={{...resultValueStyle, color: 'white', fontSize: '1.5rem'}}>{formatCurrency(loanResults.total)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'compound' && (
            <div>
              <form onSubmit={handleCompoundCalculate}>
                <div style={formGridStyle}>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("initialInvestment")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={compoundInputs.principal}
                      onChange={(e) => setCompoundInputs({...compoundInputs, principal: parseFloat(e.target.value)})}
                      min="0"
                      step="100"
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("annualInterestRate")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={compoundInputs.rate}
                      onChange={(e) => setCompoundInputs({...compoundInputs, rate: parseFloat(e.target.value)})}
                      min="0"
                      max="100"
                      step="0.1"
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("timePeriodYears")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={compoundInputs.years}
                      onChange={(e) => setCompoundInputs({...compoundInputs, years: parseFloat(e.target.value)})}
                      min="1"
                      max="50"
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("compoundFrequency")}</label>
                    <select
                      style={inputStyle}
                      value={compoundInputs.frequency}
                      onChange={(e) => setCompoundInputs({...compoundInputs, frequency: parseFloat(e.target.value)})}
                    >
                      <option value="1">{t("annually")}</option>
                      <option value="2">{t("semiAnnually")}</option>
                      <option value="4">{t("quarterly")}</option>
                      <option value="12">{t("monthly")}</option>
                      <option value="365">{t("daily")}</option>
                    </select>
                  </div>
                </div>
                <button type="submit" style={calculateBtnStyle}>{t("calculateCompoundInterest")}</button>
              </form>
              {compoundResults && (
                <div style={resultsStyle}>
                  <h4 style={h3Style}>{t("compoundInterestResults")}</h4>
                  <div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("initialInvestment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(compoundResults.initial)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("totalInterestEarned")}</span>
                      <span style={resultValueStyle}>{formatCurrency(compoundResults.earned)}</span>
                    </div>
                    <div style={resultHighlightStyle}>
                      <span style={{...resultLabelStyle, color: 'rgba(255, 255, 255, 0.9)'}}>{t("futureValue")}</span>
                      <span style={{...resultValueStyle, color: 'white', fontSize: '1.5rem'}}>{formatCurrency(compoundResults.future)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'mortgage' && (
            <div>
              <form onSubmit={handleMortgageCalculate}>
                <div style={formGridStyle}>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("homePrice")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={mortgageInputs.price}
                      onChange={(e) => setMortgageInputs({...mortgageInputs, price: parseFloat(e.target.value)})}
                      min="0"
                      step="1000"
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("downPayment")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={mortgageInputs.down}
                      onChange={(e) => setMortgageInputs({...mortgageInputs, down: parseFloat(e.target.value)})}
                      min="0"
                      step="1000"
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("interestRate")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={mortgageInputs.rate}
                      onChange={(e) => setMortgageInputs({...mortgageInputs, rate: parseFloat(e.target.value)})}
                      min="0"
                      max="100"
                      step="0.1"
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("loanTermYears")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={mortgageInputs.term}
                      onChange={(e) => setMortgageInputs({...mortgageInputs, term: parseFloat(e.target.value)})}
                      min="1"
                      max="50"
                      required
                    />
                  </div>
                </div>
                <button type="submit" style={calculateBtnStyle}>{t("calculateMortgage")}</button>
              </form>
              {mortgageResults && (
                <div style={resultsStyle}>
                  <h4 style={h3Style}>{t("mortgageResults")}</h4>
                  <div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("loanAmount")}</span>
                      <span style={resultValueStyle}>{formatCurrency(mortgageResults.loan)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("monthlyPayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(mortgageResults.monthly)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("totalInterest")}</span>
                      <span style={resultValueStyle}>{formatCurrency(mortgageResults.interest)}</span>
                    </div>
                    <div style={resultHighlightStyle}>
                      <span style={{...resultLabelStyle, color: 'rgba(255, 255, 255, 0.9)'}}>{t("totalCost")}</span>
                      <span style={{...resultValueStyle, color: 'white', fontSize: '1.5rem'}}>{formatCurrency(mortgageResults.total)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'savings' && (
            <div>
              <form onSubmit={handleSavingsCalculate}>
                <div style={formGridStyle}>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("savingsGoal")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={savingsInputs.goal}
                      onChange={(e) => setSavingsInputs({...savingsInputs, goal: parseFloat(e.target.value)})}
                      min="0"
                      step="100"
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("initialAmount")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={savingsInputs.initial}
                      onChange={(e) => setSavingsInputs({...savingsInputs, initial: parseFloat(e.target.value)})}
                      min="0"
                      step="100"
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("monthlyContribution")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={savingsInputs.monthly}
                      onChange={(e) => setSavingsInputs({...savingsInputs, monthly: parseFloat(e.target.value)})}
                      min="0"
                      step="10"
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>{t("annualInterestRate")}</label>
                    <input
                      type="number"
                      style={inputStyle}
                      value={savingsInputs.rate}
                      onChange={(e) => setSavingsInputs({...savingsInputs, rate: parseFloat(e.target.value)})}
                      min="0"
                      max="100"
                      step="0.1"
                      required
                    />
                  </div>
                </div>
                <button type="submit" style={calculateBtnStyle}>{t("calculateSavingsTimeline")}</button>
              </form>
              {savingsResults && (
                <div style={resultsStyle}>
                  <h4 style={h3Style}>{t("savingsGoalResults")}</h4>
                  <div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("timeToReachGoal")}</span>
                      <span style={resultValueStyle}>{savingsResults.time}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("totalContributions")}</span>
                      <span style={resultValueStyle}>{formatCurrency(savingsResults.contributions)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t("interestEarned")}</span>
                      <span style={resultValueStyle}>{formatCurrency(savingsResults.interest)}</span>
                    </div>
                    <div style={resultHighlightStyle}>
                      <span style={{...resultLabelStyle, color: 'rgba(255, 255, 255, 0.9)'}}>{t("finalAmount")}</span>
                      <span style={{...resultValueStyle, color: 'white', fontSize: '1.5rem'}}>{formatCurrency(savingsResults.final)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToUseStep1")}</li>
          <li style={listItemStyle}>{t("howToUseStep2")}</li>
          <li style={listItemStyle}>{t("howToUseStep3")}</li>
          <li style={listItemStyle}>{t("howToUseStep4")}</li>
          <li style={listItemStyle}>{t("howToUseStep5")}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>

        <h3 style={h3Style}>{t("insightsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("insightsContent1")}</p>

        <h3 style={h3Style}>{t("insightsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("insightsContent2")}</p>

        <h3 style={h3Style}>{t("insightsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("insightsContent3")}</p>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={h3Style}>{t("detailsSubtitle1")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("detailsItem1")}</li>
          <li style={listItemStyle}>{t("detailsItem2")}</li>
          <li style={listItemStyle}>{t("detailsItem3")}</li>
          <li style={listItemStyle}>{t("detailsItem4")}</li>
          <li style={listItemStyle}>{t("detailsItem5")}</li>
        </ul>

        <h3 style={h3Style}>{t("detailsSubtitle2")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("detailsBenefit1")}</li>
          <li style={listItemStyle}>{t("detailsBenefit2")}</li>
          <li style={listItemStyle}>{t("detailsBenefit3")}</li>
          <li style={listItemStyle}>{t("detailsBenefit4")}</li>
          <li style={listItemStyle}>{t("detailsBenefit5")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t("faqQuestion1")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer1")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t("faqQuestion2")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer2")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t("faqQuestion3")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer3")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t("faqQuestion4")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer4")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t("faqQuestion5")}</h3>
          <p style={paragraphStyle}>{t("faqAnswer5")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ol style={listStyle}>
          <li style={referenceStyle}>
            <a href="https://www.adityabirlacapital.com/abc-of-money/role-of-calculators-in-financial-planning" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference1")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://florabankandtrust.com/consumer-resources/financial-wellness-information/benefits-of-using-financial-calculators" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference2")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://www.hp.com/us-en/shop/tech-takes/top-3-uses-for-financial-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference3")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://www.digitalgrowth.com/podcast/articles/banks-and-credit-unions-financial-calculators" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference4")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="http://archives.cpajournal.com/2003/0703/features/f073003.htm" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference5")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://www.fintactix.com/financial-calculators-for-websites/insights/using-financial-calculators-as-a-lead-generation-tool-on-your-website" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference6")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://thefinancialbrand.com/news/digital-marketing-banking/why-calculators-are-essential-to-your-financial-institutions-seo-strategy-116690" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference7")}
            </a>
          </li>
          <li style={referenceStyle}>
            <a href="https://www.aarp.org/money/personal-finance/spending-less-calculator/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference8")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}